/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { lastAccessedGitRef } from "app/areas/projects/context/LastAccessedGitRef";
import * as React from "react";
import { createContext } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router";
import { generatePath } from "react-router-dom";
import ProjectContextRepository from "~/client/repositories/projectContextRepository";
import type { ProjectResource, ProjectSummary, GitRefResource } from "~/client/resources";
import { HasVersionControlledPersistenceSettings } from "~/client/resources";
import { IsDefaultBranch } from "~/client/resources/versionControlledResource";
import type { GitRef } from "~/client/resources/versionControlledResource";
import { repository, client } from "~/clientInstance";
import type { DoBusyTask } from "~/components/DataBaseComponent";
import { useRequiredContext } from "~/hooks";
import { RecentProjects } from "~/utils/RecentProjects/RecentProjects";
import { logger } from "~/utils/logging/logger";
import type { BranchSpecifier } from "../components/ProjectsRoutes/BranchSpecifier";
import { ShouldUseDefaultBranch, UseDefaultBranch } from "../components/ProjectsRoutes/BranchSpecifier";

type ProjectContextProviderProps = {
    doBusyTask: DoBusyTask;
    projectIdOrSlug: string;
    gitRef: BranchSpecifier;
    children: (props: ProjectContextProps) => React.ReactNode;
};

export interface BranchError {
    branchName: string;
    errors: string[];
}

export interface ProjectContextState {
    model: Readonly<ProjectResource>;
    summary: Readonly<ProjectSummary>;
    gitRef: Readonly<GitRefResource> | undefined;
    projectContextRepository: ProjectContextRepository;
    branchError: BranchError | undefined;
    isDefaultBranch: boolean | undefined;
    vcsErrorMessages: string[];
    onVcsError?: (e: Error) => void;
    gitVariablesHasError?: Boolean;
    gitVariablesErrorMessages: string[];
}

export interface ProjectContextActions extends ProjectLayoutActions {
    refreshModel: () => Promise<boolean>;
}

export type ProjectContextProps = ReturnType<typeof useProjectLayoutSetup>;
export const ProjectContext = createContext<ProjectContextProps | undefined>(undefined);

function modifyStateForWhenBranchNotLoaded(value: ProjectContextProps, branch: BranchSpecifier): ProjectContextProps {
    // Switching branches, we load the new branch resource asynchronously whereas the page (e.g. list of runbooks) tries
    // to reload its data. As the branch has not yet been loaded, runbooks get loaded from the old branch. We explicitly
    // set the branch to undefined here to force ProjectLayout to not render content till the branch data has been loaded.
    if (branch !== UseDefaultBranch && branch !== value.state.gitRef?.Name) {
        return {
            ...value,
            state: {
                ...value.state,
                gitRef: undefined,
            },
        };
    }
    return value;
}

const ProjectContextProvider: React.FC<ProjectContextProviderProps> = (props) => {
    const value = useProjectLayoutSetup(props.doBusyTask, props.projectIdOrSlug, props.gitRef);
    return <ProjectContext.Provider value={value}>{props.children(modifyStateForWhenBranchNotLoaded(value, props.gitRef))}</ProjectContext.Provider>;
};

export const useProjectContext = () => {
    return useRequiredContext(ProjectContext, "Project");
};

export const useOptionalProjectContext = () => {
    return React.useContext(ProjectContext);
};

const getStateUpdaters = (setState: React.Dispatch<React.SetStateAction<ProjectContextState>>) => {
    return {
        onProjectUpdated: async (project: ProjectResource, gitRef: GitRefResource | string | undefined) => {
            try {
                const summary = await repository.Projects.getSummary(project, gitRef);
                setState((current) => ({ ...current, model: project, summary, isDefaultBranch: IsDefaultBranch(project, GetCanonicalName(gitRef)) }));
            } catch (ex) {
                logger.error(ex, "Error getting summary for {project}", { project, gitRef });
                const emptySummary = { HasRunbooks: false, HasDeploymentProcess: false };
                setState((current) => ({ ...current, model: project, summary: emptySummary, isDefaultBranch: IsDefaultBranch(project, GetCanonicalName(gitRef)) }));
            }
        },
        onVersionControlEnabled: async (project: ProjectResource) => {
            if (!HasVersionControlledPersistenceSettings(project.PersistenceSettings)) throw new Error("Config as Code: Trying to access a VCS Property on a non-VCS Project.");
            await lastAccessedGitRef.save(project, project.PersistenceSettings.DefaultBranch);
            const branch = await repository.Projects.getBranch(project, project.PersistenceSettings.DefaultBranch);
            setState((current) => ({ ...current, gitRef: branch, projectContextRepository: new ProjectContextRepository(client, project, branch), isDefaultBranch: IsDefaultBranch(project, GetCanonicalName(branch)) }));
        },
        onBranchSelected: async (project: ProjectResource, gitRef: GitRef) => {
            await lastAccessedGitRef.save(project, gitRef);
            const gitRefResource = await repository.Projects.getGitRef(project, gitRef);
            setState((current) => ({ ...current, gitRef: gitRefResource, projectContextRepository: new ProjectContextRepository(client, project, gitRefResource), isDefaultBranch: IsDefaultBranch(project, GetCanonicalName(gitRefResource)) }));
        },
    };
};

function GetCanonicalName(branch: GitRefResource | string | undefined): string | undefined {
    if (typeof branch === "string") {
        return branch;
    } else {
        return branch?.CanonicalName;
    }
}

const useProjectLayoutState = () => {
    return React.useState<ProjectContextState>({
        model: null!,
        summary: null!,
        projectContextRepository: null!,
        gitRef: undefined,
        branchError: undefined,
        isDefaultBranch: undefined,
        vcsErrorMessages: [],
        gitVariablesErrorMessages: [],
    });
};

const invokeBusyWithResponse = async <T,>(action: () => Promise<T>, doBusyTask: DoBusyTask) => {
    const result = action();
    await doBusyTask(() => result);
    return result;
};

const useSaveRecentAccessedProjectIdEffect = (projectId: string | null) => {
    React.useEffect(() => {
        if (projectId) {
            RecentProjects.getInstance()
                .UpdateAccessedProjectIntoLocalStorage(projectId)
                .catch((error) => logger.error(error, "Unknown error saving recently accessed project id {projectId}", { projectId }));
        }
    }, [projectId]);
};

type ProjectLayoutActions = ReturnType<typeof getStateUpdaters>;

const subscribeKey = "ProjectContextState";

const useProjectLayoutSetup = (doBusyTask: DoBusyTask, projectIdOrSlug: string, branchSpecifier: BranchSpecifier) => {
    const [state, setState] = useProjectLayoutState();
    const updaters = React.useMemo(() => getStateUpdaters(setState), [setState]);

    const match = useRouteMatch()!;
    const location = useLocation();
    const history = useHistory();

    const canChangeBranch = React.useCallback(() => match.params.hasOwnProperty("branchName"), [match]);

    const changeBranch = React.useCallback(
        (branchName: string) => {
            // Clear any errors as this will trigger a re-evaluation
            setState((current) => ({
                ...current,
                branchError: undefined,
            }));

            if (!canChangeBranch()) return;

            const newUrl = generatePath(match.path, { ...match.params, branchName });
            const newPath = newUrl + location.pathname.substr(match.url.length);

            // If the path hasn't changed, then don't bother redirecting, we might end up in a redirect loop
            if (newPath === location.pathname) return;

            history.push({ ...location, pathname: newPath });
        },
        [setState, canChangeBranch, match, location, history]
    );

    const validateGitRef = async (project: ProjectResource, branchSpecifier: string) => {
        const validationResult = await repository.Projects.validateGitRef(project, branchSpecifier);
        if (validationResult.Error) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            changeBranch(project.PersistenceSettings.DefaultBranch);
            setState((current) => ({
                ...current,
                branchError: {
                    branchName: branchSpecifier,
                    errors: [validationResult.Error!],
                },
            }));
            return null;
        }
        return validationResult.ValidatedGitRef!;
    };

    const refreshAndGetModel = React.useCallback(
        () =>
            invokeBusyWithResponse(async () => {
                const project = await repository.Projects.get(projectIdOrSlug);
                let gitRefResource: GitRefResource | undefined = undefined;

                if (HasVersionControlledPersistenceSettings(project.PersistenceSettings)) {
                    try {
                        if (ShouldUseDefaultBranch(branchSpecifier)) {
                            gitRefResource = await lastAccessedGitRef.get(project);
                        } else {
                            const validatedGitRef = await validateGitRef(project, branchSpecifier);
                            if (!validatedGitRef) {
                                // Todo @team-config-as-code: What if the error is related to the default branch?
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                return project;
                            }
                            gitRefResource = validatedGitRef;
                            await lastAccessedGitRef.save(project, gitRefResource.CanonicalName);
                        }
                    } catch (e) {
                        setState((current) => ({
                            ...current,
                            gitRef: undefined,
                            vcsErrorMessages: [e.ErrorMessage],
                            projectContextRepository: new ProjectContextRepository(client, project, gitRefResource),
                        }));
                    }
                }

                setState((current) => ({
                    ...current,
                    gitRef: gitRefResource,
                    projectContextRepository: new ProjectContextRepository(client, project, gitRefResource),
                }));

                await updaters.onProjectUpdated(project, gitRefResource);
                return project;
            }, doBusyTask),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [doBusyTask, projectIdOrSlug, setState, updaters, branchSpecifier]
    );

    const refreshModel = React.useCallback(() => refreshAndGetModel().then((x) => true), [refreshAndGetModel]);

    const refreshGitVariableErrors = async () => {
        state.projectContextRepository.Variables.get()
            .then(() => {
                setState((current) => ({ ...current, gitVariablesHasError: false, gitVariablesErrorMessages: [] }));
            })
            .catch((exception) => {
                const errorMessages = [exception.ErrorMessage];

                // Add any additional details about the error. This is often where the really useful stuff is.
                if (exception.Errors) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    exception.Errors.forEach((error: any) => {
                        if (error) {
                            errorMessages.push(error);
                        }
                    });
                }

                setState((current) => ({ ...current, gitVariablesHasError: true, gitVariablesErrorMessages: errorMessages }));
            });
    };

    React.useEffect(() => {
        repository.Projects.subscribeToDataModifications(subscribeKey, (project) => {
            setState((previous) => {
                if (previous.model && previous.model.Id === project.Id) {
                    return { ...previous, model: project };
                } else {
                    return previous;
                }
            });
        });

        return () => {
            repository.Projects.unsubscribeFromDataModifications(subscribeKey);
        };
    }, [setState]);

    React.useEffect(() => {
        // eslint-disable-next-line: no-floating-promises
        refreshAndGetModel();
    }, [refreshAndGetModel]);

    useSaveRecentAccessedProjectIdEffect(state.model && state.model.Id);

    const supportedActions = {
        refreshAndGetModel,
        refreshModel,
        changeGitRef: changeBranch,
        refreshGitVariableErrors,
        ...updaters,
    };

    return {
        actions: supportedActions,
        state,
        setState,
    };
};

type ProjectContextConsumerProps = Parameters<typeof ProjectContext.Consumer>[0];
const ProjectContextConsumer: React.SFC<ProjectContextConsumerProps> = ({ children }) => {
    const context = useProjectContext();
    return <React.Fragment>{children(context)}</React.Fragment>;
};

export { ProjectContextProvider, ProjectContextConsumer };
